"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  Checkbox,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material"
import { Add, Delete } from "@mui/icons-material"
import LoadingButton from "../common/LoadingButton"

// Import the error handler
import { handleApiError } from "../../utils/error-handler"

const PlanForm = ({ plan = {}, onSubmit, loading = false, isEdit = false }) => {
  const navigate = useNavigate()
  const entitlements = plan.entitlements || {}
  const [formData, setFormData] = useState({
    name: plan.name,
    description: plan.description,
    features: Array.isArray(plan.features) ? plan.features : [],
    planType: plan.planType || "regular",
    planCategory: plan.planCategory || "regular",
    monthlyPrice: plan.monthlyPrice || 0,
    annualPrice: plan.annualPrice || 0,
    lifetimePrice: plan.lifetimePrice,
    currency: plan.currency || "USD",
    trialDays: plan.trialDays || 0,
    isActive: plan.isActive !== undefined ? plan.isActive : true,
    isPublic: plan.isPublic !== undefined ? plan.isPublic : true,
    isDefault: plan.isDefault !== undefined ? plan.isDefault : false,
    creditAllowance: plan.creditAllowance || 0,
    maxUsers: plan.maxUsers ?? 5,
    maxWorkspaces: plan.maxWorkspaces ?? 1,
    sortOrder: plan.sortOrder || 0,
    providerId: plan.providerId,
    provider: plan.provider || "stripe",
    tier_level: plan.tier_level || 0,
    // Entitlements (per-plan rules that may differ by tier)
    executionHistoryDays: entitlements.executionHistoryDays ?? "",
    maxFileSizeMb: entitlements.maxFileSizeMb ?? "",
    icpCreditsPerRecord: entitlements.icpCreditsPerRecord ?? "",
    aiAgentCreditsPerNode: entitlements.aiAgentCreditsPerNode ?? "",
    customAiAgentCreation: entitlements.customAiAgentCreation ?? false,
    operationsUsageFlexibility: entitlements.operationsUsageFlexibility ?? false,
    removeBranding: entitlements.removeBranding ?? false,
    folderFilePermissions: entitlements.folderFilePermissions ?? false,
    // Transient, edit-only: propagate this plan's entitlements to existing subscribers
    applyToExistingSubscribers: false,
    masterPassword: "",
  })

  const [newFeature, setNewFeature] = useState("")

  // Show/hide price fields based on plan type
  const [showPriceFields, setShowPriceFields] = useState({
    monthly: true,
    annual: true,
    lifetime: false,
    free: false,
  })

  useEffect(() => {
    // Update visible price fields when plan type changes
    const newShowPriceFields = {
      monthly: formData.planType === "regular",
      annual: formData.planType === "regular",
      lifetime: formData.planType === "lifetime",
      free: false,
    }
    setShowPriceFields(newShowPriceFields)
  }, [formData.planType])

  const handleKeyDown = (e, isIntegerOnly = false) => {
    // Allow: backspace, delete, tab, escape, enter
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter']
    if (allowedKeys.includes(e.key)) return

    // For non-integer fields, also allow decimal point
    if (!isIntegerOnly && e.key === '.') return

    // Allow only numbers
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault()
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    
    // List of numeric fields that need validation
    const numericFields = [
      'monthlyPrice',
      'annualPrice',
      'lifetimePrice',
      'trialDays',
      'creditAllowance',
      'maxUsers',
      'maxWorkspaces',
      'sortOrder',
      'executionHistoryDays',
      'maxFileSizeMb',
      'icpCreditsPerRecord',
      'aiAgentCreditsPerNode',
    ]

    // If it's a numeric field, validate the input
    if (numericFields.includes(name)) {
      // Allow empty value for backspace/delete
      if (value === '') {
        setFormData({
          ...formData,
          [name]: ''
        })
        return
      }

      // Only allow numbers and a single decimal point
      const numericValue = value.replace(/[^0-9.]/g, '')
      const parts = numericValue.split('.')
      
      // If there's more than one decimal point, keep only the first one
      const validValue = parts.length > 2 ? parts[0] + '.' + parts[1] : numericValue
      
      // Only update if we have a valid number
      if (validValue !== '') {
        setFormData({
          ...formData,
          [name]: validValue
        })
      }
    } else {
      // For non-numeric fields, update as normal
      setFormData({
        ...formData,
        [name]: value,
      })
    }
  }

  const handleFocus = (e) => {
    const { name, value } = e.target
    // If the value is 0, clear it to allow new input
    if (value === '0') {
      setFormData({
        ...formData,
        [name]: ''
      })
    }
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    // If the field is empty, set it back to 0
    if (value === '') {
      setFormData({
        ...formData,
        [name]: '0'
      })
    }
  }

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target
    setFormData({
      ...formData,
      [name]: checked,
    })
  }

  // -1 is the unlimited sentinel (same convention as credit_limit); never surfaced to the user
  const isUnlimited = (value) => Number(value) === -1

  const handleUnlimitedChange = (field, fallback) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.checked ? -1 : fallback,
    })
  }

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()],
      })
      setNewFeature("")
    }
  }

  const handleRemoveFeature = (index) => {
    const updatedFeatures = [...formData.features]
    updatedFeatures.splice(index, 1)
    setFormData({
      ...formData,
      features: updatedFeatures,
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    // Set appropriate prices based on plan type
    const processedData = { ...formData }

    if (formData.planType === "free") {
      processedData.monthlyPrice = 0
      processedData.annualPrice = 0
      processedData.lifetimePrice = 0
    } else if (formData.planType === "lifetime") {
      processedData.monthlyPrice = 0
      processedData.annualPrice = 0
    }

    // Build entitlements object from entitlements-related fields
    const entitlementsPayload = {}

    if (formData.executionHistoryDays !== "" && formData.executionHistoryDays !== null) {
      entitlementsPayload.executionHistoryDays = Number(formData.executionHistoryDays)
    }
    if (formData.maxFileSizeMb !== "" && formData.maxFileSizeMb !== null) {
      entitlementsPayload.maxFileSizeMb = Number(formData.maxFileSizeMb)
    }
    if (formData.icpCreditsPerRecord !== "" && formData.icpCreditsPerRecord !== null) {
      entitlementsPayload.icpCreditsPerRecord = Number(formData.icpCreditsPerRecord)
    }
    if (formData.aiAgentCreditsPerNode !== "" && formData.aiAgentCreditsPerNode !== null) {
      entitlementsPayload.aiAgentCreditsPerNode = Number(formData.aiAgentCreditsPerNode)
    }

    // Booleans are always safe to include
    entitlementsPayload.customAiAgentCreation = !!formData.customAiAgentCreation
    entitlementsPayload.operationsUsageFlexibility = !!formData.operationsUsageFlexibility
    entitlementsPayload.removeBranding = !!formData.removeBranding
    entitlementsPayload.folderFilePermissions = !!formData.folderFilePermissions

    if (Object.keys(entitlementsPayload).length > 0) {
      processedData.entitlements = entitlementsPayload
    }

    try {
      // backend expects `provider`
      if (!processedData.provider && processedData.providerType) {
        processedData.provider = processedData.providerType
        delete processedData.providerType
      }
      // Ensure providerId is only sent for appsumo
      if (processedData.provider !== 'appsumo') {
        delete processedData.providerId
      }
      onSubmit(processedData)
    } catch (error) {
      console.error("Form submission error:", handleApiError(error))
    }
  }

  const handleCancel = () => {
    navigate("/plans")
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Plan Details
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField required fullWidth label="Plan Name" name="name" value={formData.name} onChange={handleChange} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Description"
              name="description"
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel id="plan-type-label">Plan Type</InputLabel>
              <Select
                labelId="plan-type-label"
                id="planType"
                name="planType"
                value={formData.planType}
                label="Plan Type"
                onChange={handleChange}
              >
                <MenuItem value="regular">Regular</MenuItem>
                <MenuItem value="lifetime">Lifetime</MenuItem>
                <MenuItem value="free">Free Forever</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel id="plan-category-label">Plan Category</InputLabel>
              <Select
                labelId="plan-category-label"
                id="planCategory"
                name="planCategory"
                value={formData.planCategory}
                label="Plan Category"
                onChange={handleChange}
              >
                <MenuItem value="regular">Regular</MenuItem>
                <MenuItem value="friends_family">Friends & Family</MenuItem>
                <MenuItem value="unlimited">Unlimited</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Currency" name="currency" value={formData.currency} onChange={handleChange} />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel id="provider-type-label">Provider</InputLabel>
              <Select
                labelId="provider-type-label"
                id="provider"
                name="provider"
                value={formData.provider}
                label="Provider"
                onChange={handleChange}
              >
                <MenuItem value="stripe">Stripe</MenuItem>
                <MenuItem value="appsumo">AppSumo</MenuItem>
                <MenuItem value="rockethub">RocketHub</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField 
              fullWidth 
              label="Provider ID" 
              name="providerId" 
              value={formData.providerId} 
              onChange={handleChange}
              disabled={["stripe", "rockethub"].includes(formData.provider)}
              helperText={["stripe", "rockethub"].includes(formData.provider) ? `Provider ID is automatically managed for ${formData.provider}` : `${formData.provider} plan identifier`}
            />
          </Grid>

          {showPriceFields.monthly && (
            <Grid item xs={12} md={6}>
              <TextField
                required={formData.planType === "regular"}
                fullWidth
                label="Monthly Price"
                name="monthlyPrice"
                type="number"
                inputProps={{ min: "0" }}
                onKeyDown={(e) => handleKeyDown(e, false)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                value={formData.monthlyPrice}
                onChange={handleChange}
              />
            </Grid>
          )}

          {showPriceFields.annual && (
            <Grid item xs={12} md={6}>
              <TextField
                required={formData.planType === "regular"}
                fullWidth
                label="Annual Price"
                name="annualPrice"
                type="number"
                inputProps={{ min: "0" }}
                onKeyDown={(e) => handleKeyDown(e, false)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                value={formData.annualPrice}
                onChange={handleChange}
              />
            </Grid>
          )}

          {showPriceFields.lifetime && (
            <Grid item xs={12} md={6}>
              <TextField
                required={formData.planType === "lifetime"}
                fullWidth
                label="Lifetime Price"
                name="lifetimePrice"
                type="number"
                inputProps={{ min: "0" }}
                onKeyDown={(e) => handleKeyDown(e, false)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                value={formData.lifetimePrice}
                onChange={handleChange}
              />
            </Grid>
          )}

          {formData.planType === "regular" && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Trial Days"
                name="trialDays"
                type="number"
                inputProps={{ min: "0", step: "1" }}
                onKeyDown={(e) => handleKeyDown(e, true)}
                value={formData.trialDays}
                onChange={handleChange}
                helperText="0 means no trial"
              />
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Credit Allowance"
              name="creditAllowance"
              type="number"
              inputProps={{ min: "0", step: "1" }}
              onKeyDown={(e) => handleKeyDown(e, true)}
              value={formData.creditAllowance}
              onChange={handleChange}
              helperText={formData.planCategory === 'unlimited' ? 'Ignored for unlimited plans' : 'Credits included with this plan'}
              disabled={formData.planCategory === 'unlimited'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <TextField
                fullWidth
                label="Max Users"
                name="maxUsers"
                type="number"
                inputProps={{ min: "0", step: "1" }}
                onKeyDown={(e) => handleKeyDown(e, true)}
                value={isUnlimited(formData.maxUsers) ? "" : formData.maxUsers}
                onChange={handleChange}
                disabled={isUnlimited(formData.maxUsers)}
                placeholder={isUnlimited(formData.maxUsers) ? "Unlimited" : undefined}
                helperText={isUnlimited(formData.maxUsers) ? "No user limit" : "Maximum number of users allowed"}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isUnlimited(formData.maxUsers)}
                    onChange={handleUnlimitedChange("maxUsers", 5)}
                    name="unlimitedUsers"
                    color="primary"
                  />
                }
                label="Unlimited"
                sx={{ mt: 1, mr: 0, whiteSpace: "nowrap" }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <TextField
                fullWidth
                label="Max Workspaces"
                name="maxWorkspaces"
                type="number"
                inputProps={{ min: "0", step: "1" }}
                onKeyDown={(e) => handleKeyDown(e, true)}
                value={isUnlimited(formData.maxWorkspaces) ? "" : formData.maxWorkspaces}
                onChange={handleChange}
                disabled={isUnlimited(formData.maxWorkspaces)}
                placeholder={isUnlimited(formData.maxWorkspaces) ? "Unlimited" : undefined}
                helperText={
                  isUnlimited(formData.maxWorkspaces) ? "No workspace limit" : "Maximum number of workspaces allowed"
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isUnlimited(formData.maxWorkspaces)}
                    onChange={handleUnlimitedChange("maxWorkspaces", 1)}
                    name="unlimitedWorkspaces"
                    color="primary"
                  />
                }
                label="Unlimited"
                sx={{ mt: 1, mr: 0, whiteSpace: "nowrap" }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Sort Order"
              name="sortOrder"
              type="number"
              inputProps={{ min: "0", step: "1" }}
              onKeyDown={(e) => handleKeyDown(e, true)}
              value={formData.sortOrder}
              onChange={handleChange}
              helperText="Display order (lower numbers first)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tier Level"
              name="tier_level"
              type="number"
              inputProps={{ min: "0", step: "10" }}
              onKeyDown={(e) => handleKeyDown(e, true)}
              value={formData.tier_level}
              onChange={handleChange}
              helperText="Plan hierarchy level (higher = more premium, used for upgrade/downgrade logic)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Execution History (days)"
              name="executionHistoryDays"
              type="number"
              inputProps={{ min: "0", step: "1" }}
              onKeyDown={(e) => handleKeyDown(e, true)}
              value={formData.executionHistoryDays}
              onChange={handleChange}
              helperText="How long to retain execution history"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Maximum File Size (MB)"
              name="maxFileSizeMb"
              type="number"
              inputProps={{ min: "0", step: "1" }}
              onKeyDown={(e) => handleKeyDown(e, true)}
              value={formData.maxFileSizeMb}
              onChange={handleChange}
              helperText="Maximum upload size allowed for this plan"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="ICP Credits per Record"
              name="icpCreditsPerRecord"
              type="number"
              inputProps={{ min: "0", step: "1" }}
              onKeyDown={(e) => handleKeyDown(e, true)}
              value={formData.icpCreditsPerRecord}
              onChange={handleChange}
              helperText="Leave empty for custom or default"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="AI Agent Credits per Node"
              name="aiAgentCreditsPerNode"
              type="number"
              inputProps={{ min: "0", step: "1" }}
              onKeyDown={(e) => handleKeyDown(e, true)}
              value={formData.aiAgentCreditsPerNode}
              onChange={handleChange}
              helperText="Leave empty for custom or default"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <FormControlLabel
                control={
                  <Switch checked={formData.isActive} onChange={handleSwitchChange} name="isActive" color="primary" />
                }
                label="Active"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch checked={formData.isPublic} onChange={handleSwitchChange} name="isPublic" color="primary" />
                }
                label="Publicly Visible"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Public plans are visible in the catalog
              </Typography>
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch checked={formData.isDefault} onChange={handleSwitchChange} name="isDefault" color="primary" />
                }
                label="Default Plan"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                New users will be automatically subscribed to this plan
              </Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.customAiAgentCreation}
                    onChange={handleSwitchChange}
                    name="customAiAgentCreation"
                    color="primary"
                  />
                }
                label="Custom AI Agent Creation"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.operationsUsageFlexibility}
                    onChange={handleSwitchChange}
                    name="operationsUsageFlexibility"
                    color="primary"
                  />
                }
                label="Operations Usage Flexibility"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.removeBranding}
                    onChange={handleSwitchChange}
                    name="removeBranding"
                    color="primary"
                  />
                }
                label="Remove TinyCommand Branding"
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.folderFilePermissions}
                    onChange={handleSwitchChange}
                    name="folderFilePermissions"
                    color="primary"
                  />
                }
                label="Folder/File Permissions"
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Features
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: "flex", mb: 2 }}>
          <TextField
            fullWidth
            label="Add Feature"
            value={newFeature}
            onChange={(e) => setNewFeature(e.target.value)}
            placeholder="Enter a feature"
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                handleAddFeature()
              }
            }}
          />
          <IconButton color="primary" onClick={handleAddFeature} sx={{ ml: 1 }}>
            <Add />
          </IconButton>
        </Box>

        <List>
          {formData.features.length > 0 ? (
            formData.features.map((feature, index) => (
              <ListItem key={index} divider={index < formData.features.length - 1}>
                <ListItemText primary={feature} />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => handleRemoveFeature(index)} color="error">
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              No features added yet. Add some features to make your plan more attractive.
            </Typography>
          )}
        </List>
      </Paper>

      {isEdit && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.applyToExistingSubscribers}
                onChange={handleSwitchChange}
                name="applyToExistingSubscribers"
                color="primary"
              />
            }
            label="Apply changes to existing subscribers"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Re-applies this plan's features and limits to active & trial subscribers. Requires the master password.
          </Typography>
          {formData.applyToExistingSubscribers && (
            <TextField
              fullWidth
              type="password"
              label="Master password"
              name="masterPassword"
              value={formData.masterPassword}
              onChange={handleChange}
              sx={{ mt: 2 }}
            />
          )}
        </Paper>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
        <LoadingButton type="submit" variant="contained" loading={loading}>
          {isEdit ? "Update Plan" : "Create Plan"}
        </LoadingButton>
      </Box>
    </Box>
  )
}

export default PlanForm

